import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Reject if `p` hasn't settled within `ms`.
 *
 * The failure this guards against isn't a slow answer, it's no answer: when
 * the project's database stops serving, PostgREST holds the connection open
 * and the query never settles, so an `await` on it waits forever.
 *
 * Pass `onTimeout` to actually cancel the underlying work — rejecting only
 * stops us listening, it doesn't stop the request, and an un-cancelled
 * request is free to come back later and overwrite a newer answer. Supabase
 * queries take `.abortSignal(controller.signal)`, so the caller can abort.
 */
export function withTimeout<T>(
  p: PromiseLike<T>,
  ms: number,
  onTimeout?: () => void,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      try {
        onTimeout?.();
      } catch {
        // cancelling is best-effort; the timeout still has to fire.
      }
      reject(new Error(`timed out after ${ms}ms`));
    }, ms);
    Promise.resolve(p).then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });
}
